import { NormalizedPoint } from '../../../../../models/chat.models';
import { displayToPage, pageToDisplay, PageRotation } from '../../../../../shared/page-region/region-geometry';
import { PDF_POINTS_PER_INCH } from './measure-utils';

/**
 * Manual page-scale calibration: the model and the arithmetic (Issue #2442).
 *
 * The Preview ruler normally takes its drawing scale from an annotation detected in the page
 * transcript. A reviewer who finds that scale missing or wrong traces a dimension printed on the
 * plan and types its real length. That pair is the page's manual calibration, and it takes
 * precedence over the detected scale.
 *
 * Two coordinate spaces meet here, the same two Select area for chat uses. *Display* space is what
 * the reviewer sees: 0–1 across the rendered page, already turned by the page's rotation. *Page*
 * space is 0–1 across the page before rotation, origin top-left. The reviewer traces in display
 * space; the reference is stored in page space, so it survives the page being turned.
 *
 * The canonical value is a ratio, real-world inches per PDF point. It depends only on the page's
 * own coordinates, so zoom, device pixel ratio, the size of the rendered canvas and a later fix to
 * the page-size correction cannot change what a measurement reads.
 *
 * One reference line fixes a single ratio that applies equally in every direction. It cannot
 * correct a scan that was stretched differently along its width and height.
 *
 * Everything in this file is a pure function with no Angular or DOM dependency. The constants
 * mirror `PageScaleMath.java`; the server recomputes and enforces them when a calibration is saved.
 */

/** Coordinate space of stored endpoints: fractions of the unrotated page, origin top-left. */
export const PAGE_SCALE_COORDINATE_SYSTEM = 'page-normalized-unrotated-top-left/v1';

/** Version of the transcript scale detector, recorded with the automatic candidate. */
export const TRANSCRIPT_DETECTOR_VERSION = 'transcript-regex/v1';

/** Shortest reference line accepted, a quarter of a sheet inch, in PDF points. */
export const MIN_REFERENCE_POINTS = 18;

/** Smallest and largest drawing scale factors considered plausible (1:1 to 1:6000). */
export const MIN_PLAUSIBLE_SCALE_FACTOR = 1;
export const MAX_PLAUSIBLE_SCALE_FACTOR = 6000;

/** Difference from the detected scale, in percent, above which confirmation is required. */
export const MATERIAL_DIFFERENCE_PERCENT = 5;

/**
 * Documented tolerance of a calibrated reading. Physical lengths are displayed to the nearest
 * 1/16", so the reference line reads its entered length to within half of that.
 */
export const CALIBRATED_READING_TOLERANCE_INCHES = 1 / 32;

/** A page's size in PDF points. */
export interface PageSizePoints {
  width: number;
  height: number;
}

/** A straight line between two normalized points. */
export interface NormalizedLine {
  start: NormalizedPoint;
  end: NormalizedPoint;
}

/** A reason the reviewer must confirm before a calibration is saved. */
export interface ScaleWarning {
  code: 'differs-from-automatic' | 'outside-plausible-bounds';
  message: string;
}

/** The automatically detected scale of a page. */
export interface AutomaticScale {
  /** The annotation as found, e.g. `1/4" = 1'-0"`. Null when none was detected. */
  scaleText: string | null;
  /** Real-world inches per corrected sheet inch. Null when none was detected. */
  scaleFactor: number | null;
}

/** A saved manual calibration, as the server returns it. */
export interface ManualCalibration {
  calibrationId: string;
  pageRevision: string;
  /** The traced line in unrotated page space. */
  reference: NormalizedLine;
  enteredValue: string;
  knownLengthInches: number;
  normalizedValue: string;
  referenceLengthPoints: number;
  realInchesPerPoint: number;
  drawingScaleFactor: number;
  /** Page size in points the server derived the ratio with. */
  geometry: PageSizePoints & { pageSizeCorrectionFactor: number };
  automaticCandidate: { scaleText: string; drawingScaleFactor: number } | null;
  differenceFromAutomaticPercent: number | null;
  acknowledgedWarnings: ScaleWarning[];
  note: string;
  createdBy: string;
  /** ISO-8601 time the calibration was saved. */
  createdAt: string;
}

/** Which scale is in force for a page. */
export interface PageScaleState {
  /**
   * `automatic`: no manual calibration applies. `manual`: `calibration` applies and takes
   * precedence. `invalidated`: a calibration exists but the page content changed since, so it is
   * shown for inspection only.
   */
  status: 'automatic' | 'manual' | 'invalidated';
  calibration: ManualCalibration | null;
  revision: number;
  /** Version to send back with the next change. */
  etag: string;
  currentPageRevision: string;
  message: string;
}

/** One change to a page's calibration. */
export interface PageScaleHistoryEntry {
  revision: number;
  action: 'calibrated' | 'reset';
  actor: string;
  occurredAt: string;
  calibration: ManualCalibration | null;
  note: string;
}

/** A page that has never been calibrated. */
export const AUTOMATIC_PAGE_SCALE: PageScaleState = {
  status: 'automatic',
  calibration: null,
  revision: 0,
  etag: '',
  currentPageRevision: '',
  message: '',
};

// ============================================================================
// Geometry
// ============================================================================

/** Size of the page as displayed: width and height swap when it is turned a quarter. */
export function displayedPageSize(pageSize: PageSizePoints, rotation: PageRotation): PageSizePoints {
  return rotation === 90 || rotation === 270 ? { width: pageSize.height, height: pageSize.width } : { ...pageSize };
}

/** Converts a line traced on the displayed page to unrotated page space. */
export function displayLineToPage(line: NormalizedLine, rotation: PageRotation): NormalizedLine {
  return { start: displayToPage(line.start, rotation), end: displayToPage(line.end, rotation) };
}

/** Converts a stored page-space line to where it appears on the displayed page. */
export function pageLineToDisplay(line: NormalizedLine, rotation: PageRotation): NormalizedLine {
  return { start: pageToDisplay(line.start, rotation), end: pageToDisplay(line.end, rotation) };
}

/**
 * Length of a normalized line in PDF points.
 *
 * Each axis is scaled by its own page dimension, because a fraction of the width and the same
 * fraction of the height are different distances on any page that is not square. Pass the line and
 * the page size in the same space (both unrotated, or both as displayed).
 */
export function lineLengthPoints(line: NormalizedLine, pageSize: PageSizePoints): number {
  const dx = (line.end.x - line.start.x) * pageSize.width;
  const dy = (line.end.y - line.start.y) * pageSize.height;
  return Math.hypot(dx, dy);
}

// ============================================================================
// Scale arithmetic
// ============================================================================

/** The canonical ratio: real-world inches represented by one PDF point. */
export function realInchesPerPoint(knownLengthInches: number, referenceLengthPoints: number): number {
  return knownLengthInches / referenceLengthPoints;
}

/**
 * Expresses the canonical ratio as the ruler's drawing scale factor, real-world inches per
 * corrected sheet inch.
 *
 * The ruler computes `points / 72 × correction × factor`. Dividing by the correction here cancels
 * it, so a calibrated measurement reads `points × realInchesPerPoint` whatever the correction is.
 */
export function drawingScaleFactorFromRatio(ratio: number, correctionFactor: number): number {
  return (ratio * PDF_POINTS_PER_INCH) / (correctionFactor > 0 ? correctionFactor : 1);
}

/** Signed difference of the manual factor from the automatic one, in percent. */
export function differencePercent(manualFactor: number, automaticFactor: number): number {
  return ((manualFactor - automaticFactor) / automaticFactor) * 100;
}

/** Lists the reasons a reviewer must confirm before a calibration with this factor is saved. */
export function scaleWarnings(drawingScaleFactor: number, automaticFactor: number | null): ScaleWarning[] {
  const warnings: ScaleWarning[] = [];
  if (drawingScaleFactor < MIN_PLAUSIBLE_SCALE_FACTOR || drawingScaleFactor > MAX_PLAUSIBLE_SCALE_FACTOR) {
    warnings.push({
      code: 'outside-plausible-bounds',
      message:
        `This reference gives a scale of ${describeRatio(drawingScaleFactor)}, which is outside the usual range ` +
        `for drawings (1:${MIN_PLAUSIBLE_SCALE_FACTOR} to 1:${MAX_PLAUSIBLE_SCALE_FACTOR}). Check the two ends ` +
        'of the line and the length you entered.',
    });
  }
  if (automaticFactor !== null && automaticFactor > 0) {
    const difference = differencePercent(drawingScaleFactor, automaticFactor);
    if (Math.abs(difference) > MATERIAL_DIFFERENCE_PERCENT) {
      warnings.push({
        code: 'differs-from-automatic',
        message:
          `This reference differs from the scale detected on the page by ${formatSignedPercent(difference)}. ` +
          'Confirm that the line and the length are right before applying it.',
      });
    }
  }
  return warnings;
}

/** What a reference line and a known length would produce, for the confirmation summary. */
export interface CalibrationPreview {
  /** Length of the traced line in PDF points. */
  referenceLengthPoints: number;
  /** Length of the traced line on the sheet, in corrected inches. */
  sheetInches: number;
  /** True when the line is too short to calibrate from. */
  tooShort: boolean;
  /** The derived values; null when the line is too short or no valid length was entered. */
  derived: {
    realInchesPerPoint: number;
    drawingScaleFactor: number;
    /** e.g. `1:48 (1/4" = 1'-0")`. */
    scaleLabel: string;
    /** Signed difference from the detected scale in percent; null when none was detected. */
    differencePercent: number | null;
    warnings: ScaleWarning[];
  } | null;
}

/**
 * Works out what a traced line and a typed length would produce, before anything is saved.
 *
 * @param line the traced line in unrotated page space
 * @param pageSize the unrotated page size in points
 * @param knownLengthInches the typed length, or null while it is empty or invalid
 * @param correctionFactor the page's scanned-PDF correction factor
 * @param automatic the detected scale
 */
export function previewCalibration(
  line: NormalizedLine,
  pageSize: PageSizePoints,
  knownLengthInches: number | null,
  correctionFactor: number,
  automatic: AutomaticScale
): CalibrationPreview {
  const referenceLengthPoints = lineLengthPoints(line, pageSize);
  const tooShort = !Number.isFinite(referenceLengthPoints) || referenceLengthPoints < MIN_REFERENCE_POINTS;
  const preview: CalibrationPreview = {
    referenceLengthPoints,
    sheetInches: (referenceLengthPoints / PDF_POINTS_PER_INCH) * (correctionFactor > 0 ? correctionFactor : 1),
    tooShort,
    derived: null,
  };
  if (tooShort || knownLengthInches === null || !(knownLengthInches > 0)) {
    return preview;
  }
  const ratio = realInchesPerPoint(knownLengthInches, referenceLengthPoints);
  const factor = drawingScaleFactorFromRatio(ratio, correctionFactor);
  const automaticFactor = automatic.scaleFactor !== null && automatic.scaleFactor > 0 ? automatic.scaleFactor : null;
  preview.derived = {
    realInchesPerPoint: ratio,
    drawingScaleFactor: factor,
    scaleLabel: describeScaleFactor(factor),
    differencePercent: automaticFactor === null ? null : differencePercent(factor, automaticFactor),
    warnings: scaleWarnings(factor, automaticFactor),
  };
  return preview;
}

// ============================================================================
// Precedence: which scale the ruler uses
// ============================================================================

/** The scale the measurement tools use for the page on screen. */
export interface EffectiveScale {
  /** `manual`: the saved calibration. `automatic`: the detected annotation. `none`: neither. */
  source: 'manual' | 'automatic' | 'none';
  /** Real-world inches per corrected sheet inch, or null when there is no scale. */
  scaleFactor: number | null;
  /** Short description for the result card, e.g. `Manually calibrated · 1:48`. */
  label: string;
  /** True while the reviewer is temporarily viewing the detected scale instead of the manual one. */
  comparing: boolean;
}

/**
 * The ruler factor for a saved calibration on the page as this browser renders it.
 *
 * The factor is recomputed from the stored reference line and the browser's own page size, rather
 * than read from the stored ratio. The reference is the source of truth: if this browser and the
 * server ever disagree about a page's size in points, the reference line still reads exactly its
 * entered length here. When the page size is not known yet, the stored ratio is used.
 */
export function manualDrawingScaleFactor(
  calibration: ManualCalibration,
  pageSize: PageSizePoints | null,
  correctionFactor: number
): number {
  if (pageSize && pageSize.width > 0 && pageSize.height > 0) {
    const length = lineLengthPoints(calibration.reference, pageSize);
    if (length > 0 && calibration.knownLengthInches > 0) {
      return drawingScaleFactorFromRatio(realInchesPerPoint(calibration.knownLengthInches, length), correctionFactor);
    }
  }
  return drawingScaleFactorFromRatio(calibration.realInchesPerPoint, correctionFactor);
}

/**
 * Decides which scale applies.
 *
 * A manual calibration that is still valid for the page content takes precedence over the detected
 * scale. An invalidated calibration is never applied. While `compareWithAutomatic` is on, the
 * detected scale is used instead so the reviewer can see the difference; nothing is saved.
 *
 * @param pageSize the unrotated page size in points, or null when it is not known yet
 */
export function resolveEffectiveScale(options: {
  state: PageScaleState;
  automatic: AutomaticScale;
  compareWithAutomatic: boolean;
  pageSize: PageSizePoints | null;
  correctionFactor: number;
}): EffectiveScale {
  const { state, automatic, compareWithAutomatic, pageSize, correctionFactor } = options;
  const manual = state.status === 'manual' ? state.calibration : null;
  const automaticFactor = automatic.scaleFactor !== null && automatic.scaleFactor > 0 ? automatic.scaleFactor : null;

  if (manual && !compareWithAutomatic) {
    const scaleFactor = manualDrawingScaleFactor(manual, pageSize, correctionFactor);
    return {
      source: 'manual',
      scaleFactor,
      label: `Manually calibrated · ${describeRatio(scaleFactor)}`,
      comparing: false,
    };
  }
  const comparing = !!manual && compareWithAutomatic;
  if (automaticFactor !== null) {
    return {
      source: 'automatic',
      scaleFactor: automaticFactor,
      label: `${comparing ? 'Comparing: detected' : 'Detected'} ${automatic.scaleText ?? describeRatio(automaticFactor)}`,
      comparing,
    };
  }
  return {
    source: 'none',
    scaleFactor: null,
    label: comparing ? 'Comparing: no scale detected' : 'No scale detected',
    comparing,
  };
}

// ============================================================================
// Describing scales
// ============================================================================

/** Common drawing scales, by their factor (real inches per sheet inch). */
const NAMED_SCALES: [number, string][] = [
  [12, '1" = 1\'-0"'],
  [16, '3/4" = 1\'-0"'],
  [24, '1/2" = 1\'-0"'],
  [32, '3/8" = 1\'-0"'],
  [48, '1/4" = 1\'-0"'],
  [64, '3/16" = 1\'-0"'],
  [96, '1/8" = 1\'-0"'],
  [120, '1" = 10\''],
  [128, '3/32" = 1\'-0"'],
  [192, '1/16" = 1\'-0"'],
  [240, '1" = 20\''],
  [360, '1" = 30\''],
  [480, '1" = 40\''],
  [600, '1" = 50\''],
  [1200, '1" = 100\''],
];

/** Writes a factor as a ratio with enough digits to recognise it: `1:48`, `1:96.4`, `1:0.25`. */
export function describeRatio(factor: number): string {
  if (!Number.isFinite(factor) || factor <= 0) return '1:?';
  let digits = 2;
  if (factor >= 100) digits = 0;
  else if (factor >= 10) digits = 1;
  return `1:${Number(factor.toFixed(digits))}`;
}

/**
 * Writes a factor as a ratio, adding the customary name when it is within half a percent of a
 * common drawing scale: `1:48 (1/4" = 1'-0")`. A derived scale close to a standard one reassures
 * the reviewer that the reference was traced correctly.
 */
export function describeScaleFactor(factor: number): string {
  const ratio = describeRatio(factor);
  const named = NAMED_SCALES.find(([value]) => Math.abs(factor - value) / value <= 0.005);
  return named ? `${ratio} (${named[1]})` : ratio;
}

/** Writes a signed percentage with one decimal: `+2.1%`, `-50.0%`. */
export function formatSignedPercent(percent: number): string {
  return `${percent >= 0 ? '+' : ''}${percent.toFixed(1)}%`;
}

// ============================================================================
// Reading server responses
// ============================================================================

/** A point as the REST API returns it; zero coordinates may be omitted. */
interface RawPoint {
  x?: number;
  y?: number;
}

/** A calibration as the REST API returns it. Default-valued fields may be omitted. */
export interface RawPageScaleCalibration {
  calibrationId?: string;
  pageRevision?: string;
  reference?: {
    start?: RawPoint;
    end?: RawPoint;
    enteredValue?: string;
    knownLengthInches?: number;
    normalizedValue?: string;
  };
  referenceLengthPoints?: number;
  realInchesPerPoint?: number;
  drawingScaleFactor?: number;
  geometry?: { widthPoints?: number; heightPoints?: number; pageSizeCorrectionFactor?: number };
  automaticCandidate?: { scaleText?: string; drawingScaleFactor?: number };
  differenceFromAutomaticPercent?: number;
  acknowledgedWarnings?: { code?: string; message?: string }[];
  note?: string;
  createdBy?: string;
  createdAt?: string;
}

/** A page's scale state as the REST API returns it. */
export interface RawPageScaleState {
  status?: string;
  calibration?: RawPageScaleCalibration;
  revision?: number;
  etag?: string;
  currentPageRevision?: string;
  message?: string;
}

/** A history entry as the REST API returns it. */
export interface RawPageScaleHistoryEntry {
  revision?: number;
  action?: string;
  actor?: string;
  occurredAt?: string;
  calibration?: RawPageScaleCalibration;
  note?: string;
}

/** Reads a point from the API, treating a missing coordinate as zero. */
function toPoint(raw: RawPoint | undefined): NormalizedPoint {
  return { x: Number(raw?.x ?? 0), y: Number(raw?.y ?? 0) };
}

/** Converts the API's warning list to the typed model, dropping codes this client does not know. */
export function toScaleWarnings(raw: { code?: string; message?: string }[] | undefined): ScaleWarning[] {
  const warnings: ScaleWarning[] = [];
  for (const warning of raw ?? []) {
    if (warning.code === 'DIFFERS_FROM_AUTOMATIC') {
      warnings.push({ code: 'differs-from-automatic', message: warning.message ?? '' });
    } else if (warning.code === 'OUTSIDE_PLAUSIBLE_BOUNDS') {
      warnings.push({ code: 'outside-plausible-bounds', message: warning.message ?? '' });
    }
  }
  return warnings;
}

/** Converts an API calibration to the typed model. */
export function toManualCalibration(raw: RawPageScaleCalibration): ManualCalibration {
  const candidate = raw.automaticCandidate;
  const hasCandidate = !!candidate && Number(candidate.drawingScaleFactor ?? 0) > 0;
  return {
    calibrationId: raw.calibrationId ?? '',
    pageRevision: raw.pageRevision ?? '',
    reference: { start: toPoint(raw.reference?.start), end: toPoint(raw.reference?.end) },
    enteredValue: raw.reference?.enteredValue ?? '',
    knownLengthInches: Number(raw.reference?.knownLengthInches ?? 0),
    normalizedValue: raw.reference?.normalizedValue ?? '',
    referenceLengthPoints: Number(raw.referenceLengthPoints ?? 0),
    realInchesPerPoint: Number(raw.realInchesPerPoint ?? 0),
    drawingScaleFactor: Number(raw.drawingScaleFactor ?? 0),
    geometry: {
      width: Number(raw.geometry?.widthPoints ?? 0),
      height: Number(raw.geometry?.heightPoints ?? 0),
      pageSizeCorrectionFactor: Number(raw.geometry?.pageSizeCorrectionFactor ?? 1) || 1,
    },
    automaticCandidate: hasCandidate
      ? { scaleText: candidate.scaleText ?? '', drawingScaleFactor: Number(candidate.drawingScaleFactor) }
      : null,
    differenceFromAutomaticPercent: hasCandidate ? Number(raw.differenceFromAutomaticPercent ?? 0) : null,
    acknowledgedWarnings: toScaleWarnings(raw.acknowledgedWarnings),
    note: raw.note ?? '',
    createdBy: raw.createdBy ?? '',
    createdAt: raw.createdAt ?? '',
  };
}

/**
 * Converts an API scale state to the typed model.
 *
 * A status this client does not recognise is treated as automatic: when in doubt a manual
 * calibration is not applied, which is the safe direction for a measurement.
 */
export function toPageScaleState(raw: RawPageScaleState | null | undefined): PageScaleState {
  if (!raw) return { ...AUTOMATIC_PAGE_SCALE };
  let status: PageScaleState['status'] = 'automatic';
  if (raw.status === 'MANUAL_ACTIVE' && raw.calibration) status = 'manual';
  else if (raw.status === 'MANUAL_INVALIDATED_PAGE_CHANGED' && raw.calibration) status = 'invalidated';
  return {
    status,
    calibration: status === 'automatic' || !raw.calibration ? null : toManualCalibration(raw.calibration),
    revision: Number(raw.revision ?? 0),
    etag: raw.etag ?? '',
    currentPageRevision: raw.currentPageRevision ?? '',
    message: raw.message ?? '',
  };
}

/** Converts an API history entry to the typed model. */
export function toPageScaleHistoryEntry(raw: RawPageScaleHistoryEntry): PageScaleHistoryEntry {
  return {
    revision: Number(raw.revision ?? 0),
    action: raw.action === 'RESET_TO_AUTOMATIC' ? 'reset' : 'calibrated',
    actor: raw.actor ?? '',
    occurredAt: raw.occurredAt ?? '',
    calibration: raw.calibration ? toManualCalibration(raw.calibration) : null,
    note: raw.note ?? '',
  };
}
